import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { BlogPost as BlogPostType, getBlogPost, getBlogPosts } from '../../utils/blogUtils';
import { Components } from 'react-markdown';

const LazyVideo: React.FC<{ src?: string } & React.VideoHTMLAttributes<HTMLVideoElement>> = ({ src, ...props }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <video
      ref={videoRef}
      className="w-full rounded-lg my-4"
      controls
      playsInline
      preload="none"
      {...props}
    >
      {isVisible && src && <source src={src} type="video/webm" />}
      Your browser does not support the video tag.
    </video>
  );
};

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const post = id ? getBlogPost(id) : null;
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  
  const handleNavigation = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get navigation
  const allPosts = getBlogPosts();
  const currentIndex = post ? allPosts.findIndex(p => p.id === post.id) : -1;
  const navigation = {
    prev: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null,
    next: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
  };

  useEffect(() => {
    const fetchAvatar = async () => {
      if (post?.author) {
        try {
          // First try to load from public directory
          const localImage = `/${post.author}.jpg`;
          const response = await fetch(localImage);
          
          if (response.ok) {
            setAvatarUrl(localImage);
          } else {
            // If local image fails, fall back to GitHub
            const githubResponse = await fetch(`https://api.github.com/users/${post.author}`);
            if (githubResponse.ok) {
              const data = await githubResponse.json();
              setAvatarUrl(data.avatar_url);
            }
          }
        } catch (error) {
          console.error('Error fetching avatar:', error);
        }
      }
    };

    fetchAvatar();
  }, [post?.author]);

  // Process markdown content to fix list indentation
  const processMarkdownContent = (content: string) => {
    return content.replace(/^(\s{4})/gm, ' '); // Replace 4 spaces with 1 space at the start of lines
  };

  if (!post) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        <Link to="/blog" className="text-red-500 hover:text-red-400">
          Return to blog
        </Link>
      </div>
    );
  }

  return (
    <div className="flex gap-8 max-w-7xl mx-auto">
      {/* Sidebar */}
      <aside className="hidden lg:block w-72 flex-shrink-0">
        <div>
          <h3 className="text-lg font-semibold text-white mb-4 font-sans">All Posts</h3>
          <nav className="space-y-3">
            {allPosts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.id}`}
                onClick={handleNavigation}
                className={`block py-2 px-3 rounded-lg transition-colors ${
                  p.id === post?.id
                    ? 'bg-gray-800 text-red-500'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                } font-sans`}
              >
                <div className="font-medium">{p.title}</div>
                <div className="text-sm text-gray-500 mt-1">
                  {new Date(p.date).toLocaleDateString()}
                </div>
              </Link>
            ))}
          </nav>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <article className="px-4 lg:px-0">
          {/* Back to Blog Link */}
          <div className="flex justify-between items-center mb-8">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-red-500 hover:text-red-400 group transition-all duration-300"
            >
              <svg 
                className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog
            </Link>
          </div>

          {/* Post Header */}
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-sans bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-gray-400">
              <div className="flex items-center">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={post.author} 
                    className="w-8 h-8 rounded-full mr-2 border-2 border-gray-700"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-700 mr-2 flex items-center justify-center">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <span className="font-sans">{post.author}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <time className="font-sans">{new Date(post.date).toLocaleDateString()}</time>
              </div>
            </div>
          </header>

          {/* Post Content */}
          <div className={`prose prose-invert max-w-none w-full
            prose-headings:text-white 
            prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-6 prose-h1:text-white
            prose-h2:text-3xl prose-h2:font-semibold prose-h2:mb-4 prose-h2:text-white
            prose-h3:text-2xl prose-h3:font-semibold prose-h3:mb-3 prose-h3:text-white
            prose-h4:text-xl prose-h4:font-semibold prose-h4:mb-2 prose-h4:text-white
            prose-h5:text-lg prose-h5:font-semibold prose-h5:mb-2 prose-h5:text-white
            prose-h6:text-base prose-h6:font-semibold prose-h6:mb-2 prose-h6:text-white
            prose-p:text-gray-300 prose-p:leading-relaxed prose-p:max-w-none
            prose-a:text-red-500 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-white prose-strong:font-semibold
            prose-ul:list-disc prose-ul:pl-6 prose-ul:max-w-none
            prose-ol:list-decimal prose-ol:pl-6 prose-ol:max-w-none
            prose-li:text-gray-300 prose-li:mb-1
            prose-li:marker:text-gray-500
            prose-ul:prose-ul:pl-1
            prose-ul:prose-ul:prose-ul:pl-1
            prose-ol:prose-ol:pl-1
            prose-ol:prose-ol:prose-ol:pl-1
            prose-blockquote:border-l-4 prose-blockquote:border-red-500 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-300 prose-blockquote:max-w-none
            prose-hr:border-gray-700 prose-hr:my-12
            prose-img:rounded-lg prose-img:max-w-none
            prose-pre:bg-gray-900 prose-pre:p-0 prose-pre:max-w-none
            prose-code:text-red-400 prose-code:bg-gray-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
            prose-pre:code:bg-transparent prose-pre:code:p-0
            prose-pre:bg-gray-900 prose-pre:text-gray-300
            prose-pre:border prose-pre:border-gray-800 prose-pre:rounded-lg
            prose-pre:overflow-x-auto
            prose-pre:my-4
            prose-pre:p-4
            prose-pre:code:bg-transparent prose-pre:code:p-0 prose-pre:code:text-gray-300
            prose-pre:code:before:content-none prose-pre:code:after:content-none
            prose-strong:text-white
            prose-em:text-gray-300
            prose-del:text-gray-400
            prose-table:text-gray-300
            prose-th:text-white
            prose-td:text-gray-300
            prose-thead:border-gray-700
            prose-tbody:border-gray-800
          `}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                h1: ({ children }) => <h1 className="text-4xl font-bold mb-6 text-white font-sans">{children}</h1>,
                h2: ({ children }) => <h2 className="text-3xl font-semibold mb-4 text-white font-sans">{children}</h2>,
                h3: ({ children }) => <h3 className="text-2xl font-semibold mb-3 text-white font-sans">{children}</h3>,
                h4: ({ children }) => <h4 className="text-xl font-semibold mb-2 text-white font-sans">{children}</h4>,
                h5: ({ children }) => <h5 className="text-lg font-semibold mb-2 text-white font-sans">{children}</h5>,
                h6: ({ children }) => <h6 className="text-base font-semibold mb-2 text-white font-sans">{children}</h6>,
                p: ({ children }) => <p className="text-gray-300 leading-relaxed mb-4 font-sans">{children}</p>,
                hr: () => <hr className="border-gray-700 my-12" />,
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 mb-4 font-sans [&>li>ul]:ml-1 [&>li>ol]:ml-1 [&>li>ul]:list-[circle] [&>li>ul>li>ul]:list-[square] [&>li]:mb-1">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 mb-4 font-sans [&>li>ul]:ml-1 [&>li>ol]:ml-1 [&>li>ol]:list-[lower-alpha] [&>li>ol>li>ol]:list-[lower-roman] [&>li]:mb-1">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="text-gray-300 mb-1 font-sans">
                    {children}
                  </li>
                ),
                video: ({ src, ...props }) => (
                  <LazyVideo src={src || ''} {...props} />
                ),
                code({ className, children }) {
                  const match = /language-(\w+)/.exec(className || '');
                  if (match) {
                    return (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        className="rounded-lg my-4"
                        customStyle={{
                          background: '#1a1a1a',
                          padding: '1rem',
                          borderRadius: '0.5rem',
                          margin: '1rem 0',
                        }}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    );
                  }
                  return <code className="bg-gray-900 text-red-400 px-1 py-0.5 rounded font-mono">{children}</code>;
                },
                img({ src, alt }) {
                  return (
                    <img
                      src={src}
                      alt={alt}
                      className="rounded-lg shadow-lg my-4 hover:scale-[1.02] transition-transform duration-300"
                      loading="lazy"
                    />
                  );
                },
                a({ href, children }) {
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-red-500 hover:text-red-400 transition-colors font-sans"
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {processMarkdownContent(post.content)}
            </ReactMarkdown>
          </div>
          
          {/* Post Navigation */}
          <div className="mt-16 pt-8 border-t border-gray-800">
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              {navigation.prev && (
                <Link
                  to={`/blog/${navigation.prev.id}`}
                  className="group flex-1 bg-gray-900 p-6 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center text-red-500 mb-2">
                    <svg className="w-5 h-5 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Previous Post
                  </div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-red-500 transition-colors font-sans">
                    {navigation.prev.title}
                  </h3>
                </Link>
              )}
              {navigation.next && (
                <Link
                  to={`/blog/${navigation.next.id}`}
                  className="group flex-1 bg-gray-900 p-6 rounded-lg hover:bg-gray-800 transition-colors text-right"
                >
                  <div className="flex items-center justify-end text-red-500 mb-2">
                    Next Post
                    <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white group-hover:text-red-500 transition-colors font-sans">
                    {navigation.next.title}
                  </h3>
                </Link>
              )}
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost; 